import _bcp47ToEbu from "./bcp47ToEbuLanguage.js";
const bcp47ToEbu = _bcp47ToEbu;

/* Standard (canonical) metadata keys stored on eventGroup.metadata[].name.
   Decoders should always write these keys so that downstream steps only ever need to look for one name. */
const keys = {
  language: "language",
  // BCP-47 tag (eg. en, es-419)
  programTitle: "programTitle",
  episodeTitle: "episodeTitle",
  translatedProgramTitle: "translatedProgramTitle",
  translatedEpisodeTitle: "translatedEpisodeTitle",
  description: "description",
  copyright: "copyright",
  author: "author",
  translator: "translator",
  translatorContact: "translatorContact",
  editor: "editor",
  editorContact: "editorContact",
  publisher: "publisher",
  countryOfOrigin: "countryOfOrigin",
  // Country name as used by the EBU STL Country of Origin list (eg. Canada)
  subtitleListRef: "subtitleListRef",
  fontSizeRh: "fontSizeRh",
  // IMSC Rosetta font size in rh units
  defaultOutline: "defaultOutline" // outline | drop | none
};

/* Lowercase and strip everything that isn't a letter or number so "Author Name", "author_name" and "AUTHOR-NAME" all match */
function normaliseName(name) {
  return String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/* Raw source field names (normalised with normaliseName) -> canonical key */
const aliases = {
  language: keys.language,
  lang: keys.language,
  xmllang: keys.language,
  languagecode: keys.language,
  sourcelanguage: keys.language,
  title: keys.programTitle,
  ttmtitle: keys.programTitle,
  programtitle: keys.programTitle,
  programmetitle: keys.programTitle,
  programname: keys.programTitle,
  programmename: keys.programTitle,
  m608programname: keys.programTitle,
  originalprogrammetitle: keys.programTitle,
  documentoriginalprogrammetitle: keys.programTitle,
  episodetitle: keys.episodeTitle,
  originalepisodetitle: keys.episodeTitle,
  documentoriginalepisodetitle: keys.episodeTitle,
  translatedprogramtitle: keys.translatedProgramTitle,
  translatedprogrammetitle: keys.translatedProgramTitle,
  translatedepisodetitle: keys.translatedEpisodeTitle,
  description: keys.description,
  desc: keys.description,
  ttmdesc: keys.description,
  copyright: keys.copyright,
  ttmcopyright: keys.copyright,
  author: keys.author,
  authorname: keys.author,
  authorfirstname: keys.author,
  authorship: keys.author,
  creator: keys.author,
  ttmagent: keys.author,
  agent: keys.author,
  translator: keys.translator,
  translatorname: keys.translator,
  translatorsname: keys.translator,
  translatorcontact: keys.translatorContact,
  translatorcontactdetails: keys.translatorContact,
  translatorscontactdetails: keys.translatorContact,
  editor: keys.editor,
  editorname: keys.editor,
  editorsname: keys.editor,
  editorcontact: keys.editorContact,
  editorcontactdetails: keys.editorContact,
  editorscontactdetails: keys.editorContact,
  publisher: keys.publisher,
  documentpublisher: keys.publisher,
  country: keys.countryOfOrigin,
  countryoforigin: keys.countryOfOrigin,
  documentcountryoforigin: keys.countryOfOrigin,
  subtitlelistref: keys.subtitleListRef,
  subtitlelistrefcode: keys.subtitleListRef,
  subtitlelistreferencecode: keys.subtitleListRef,
  fontsizerh: keys.fontSizeRh,
  defaultoutline: keys.defaultOutline
};

/* Only accept the value if the option has no fixed list of values, or the value is in that list */
function inValues(value, optionDef) {
  if (!optionDef || optionDef.type !== "list" || !Array.isArray(optionDef.values)) {
    return value;
  }
  return optionDef.values.includes(value) ? value : null;
}
function toLanguageOption(value, optionDef) {
  if (optionDef && optionDef.type === "list") {
    return inValues(bcp47ToEbu.bcp47ToEbuLanguageName(value), optionDef);
  }
  return value;
}

/* Encoding option name -> { key, profiles?, toOption? }
   - key: canonical metadata key used to resolve the option when selected === "auto"
   - profiles: optional list of target profiles the mapping applies to (eg. when units differ between formats)
   - toOption: optional converter from the metadata value to a valid option value. Return null when there is no valid conversion. */
const optionMap = {
  "Language Code": {
    key: keys.language,
    toOption: toLanguageOption
  },
  "Program Name": {
    key: keys.programTitle
  },
  "Program Title": {
    key: keys.programTitle
  },
  "Programme Title": {
    key: keys.programTitle
  },
  "Title": {
    key: keys.programTitle
  },
  "Episode Title": {
    key: keys.episodeTitle
  },
  "Translated Program Title": {
    key: keys.translatedProgramTitle
  },
  "Translated Programme Title": {
    key: keys.translatedProgramTitle
  },
  "Translated Episode Title": {
    key: keys.translatedEpisodeTitle
  },
  "Translator's Name": {
    key: keys.translator
  },
  "Translator": {
    key: keys.translator
  },
  "Translator's Contact Details": {
    key: keys.translatorContact
  },
  "Editor's Name": {
    key: keys.editor
  },
  "Editor's Contact Details": {
    key: keys.editorContact
  },
  "Publisher": {
    key: keys.publisher
  },
  "Subtitle List Ref Code": {
    key: keys.subtitleListRef
  },
  "Country of Origin": {
    key: keys.countryOfOrigin,
    toOption: inValues
  },
  "Description": {
    key: keys.description
  },
  "Copyright": {
    key: keys.copyright
  },
  "Author": {
    key: keys.author
  },
  "Font Size": {
    key: keys.fontSizeRh,
    profiles: ["imscRosetta"]
  },
  "Default Outline": {
    key: keys.defaultOutline,
    toOption: inValues
  }
};
export default {
  keys,
  aliases,
  optionMap,
  normaliseName
};